import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, RefreshCw, AlertTriangle, CreditCard } from 'lucide-react';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 border-green-200',
  trialing: 'bg-blue-100 text-blue-800 border-blue-200',
  past_due: 'bg-amber-100 text-amber-900 border-amber-200',
  unpaid: 'bg-amber-100 text-amber-900 border-amber-200',
  canceled: 'bg-gray-100 text-gray-700 border-gray-200',
  grace_period: 'bg-gray-100 text-gray-700 border-gray-200',
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-400 text-sm">—</span>;
  return (
    <Badge variant="outline" className={STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}>
      {status.replace('_', ' ')}
    </Badge>
  );
};

export const BillingHealthTab = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyAttention, setOnlyAttention] = useState(false);

  const load = useCallback(async (sync = false) => {
    try {
      sync ? setSyncing(true) : setLoading(true);
      setError(null);
      const response = await api.get(`/admin/billing-health${sync ? '?sync=true' : ''}`);
      setData(response.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load billing health');
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-16 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const accounts = (data?.accounts || []).filter((account) => {
    const needsAttention = account.status_mismatch || account.stripe_error || account.company_status === 'payment_failed';
    if (onlyAttention && !needsAttention) return false;
    if (!search) return true;
    const haystack = `${account.company_name} ${account.admin_email} ${account.signature_url}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const cards = [
    { key: 'total', label: 'Accounts' },
    { key: 'trialing', label: 'On trial' },
    { key: 'active', label: 'Paying' },
    { key: 'past_due', label: 'Past due' },
    { key: 'canceled', label: 'Cancelled' },
    { key: 'needs_attention', label: 'Needs attention' },
  ];

  return (
    <div className="space-y-6" data-testid="billing-health-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((card) => (
          <Card key={card.key} className={card.key === 'needs_attention' && summary[card.key] ? 'border-amber-300' : ''}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold" data-testid={`billing-summary-${card.key}`}>
                {summary[card.key] ?? 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">{card.label}</p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold" data-testid="billing-summary-mrr">${summary.mrr ?? 0}</div>
            <p className="text-xs text-gray-500 mt-1">Monthly recurring</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Billing Health
            </CardTitle>
            <CardDescription>
              Live Stripe status, trial end and next charge for every account.
              {summary.not_linked ? ` ${summary.not_linked} account(s) have no Stripe subscription linked.` : ''}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setOnlyAttention(!onlyAttention)}
              data-testid="billing-filter-attention-btn"
            >
              {onlyAttention ? 'Show all' : 'Needs attention'}
            </Button>
            <Button onClick={() => load(true)} disabled={syncing} data-testid="billing-sync-btn">
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Sync from Stripe
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="billing-health-error">
              {error}
            </div>
          )}

          <Input
            placeholder="Search by company, email or signature URL"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4 max-w-md"
            data-testid="billing-health-search"
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">Account</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Stripe status</th>
                  <th className="py-2 pr-4 font-medium">Our record</th>
                  <th className="py-2 pr-4 font-medium">Trial ends</th>
                  <th className="py-2 pr-4 font-medium">Next charge</th>
                  <th className="py-2 pr-4 font-medium">Tool access</th>
                </tr>
              </thead>
              <tbody data-testid="billing-health-rows">
                {accounts.map((account) => (
                  <tr key={account.company_id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{account.company_name}</div>
                      <div className="text-xs text-gray-500">{account.admin_email}</div>
                      {account.stripe_error && (
                        <div className="text-xs text-amber-700 flex items-center gap-1 mt-1">
                          <AlertTriangle className="h-3 w-3" />
                          {account.stripe_error}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-4 capitalize">{account.plan || '—'}</td>
                    <td className="py-3 pr-4">
                      {account.linked ? (
                        <StatusBadge status={account.stripe_status} />
                      ) : (
                        <span className="text-xs text-gray-500">Not linked</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={account.local_status} />
                        {account.status_mismatch && (
                          <span className="text-xs text-amber-700" title="Our record disagrees with Stripe">out of sync</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4">{formatDate(account.trial_end)}</td>
                    <td className="py-3 pr-4">
                      {account.cancel_at_period_end ? (
                        <span className="text-gray-500">Cancels {formatDate(account.next_charge_date)}</span>
                      ) : (
                        <>
                          {formatDate(account.next_charge_date || account.current_period_end)}
                          {account.next_charge_amount != null && (
                            <span className="text-gray-500"> · ${account.next_charge_amount}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {account.company_status === 'active' ? (
                        <span className="text-green-700">Active</span>
                      ) : (
                        <span className="text-red-700 capitalize">{(account.company_status || 'unknown').replace('_', ' ')}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {accounts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-gray-500">No accounts match this view.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingHealthTab;
